import { z } from 'zod';
import {
  ApplicationEnvironmentSchema,
  ApplicationStatusSchema,
  IsoTimestampSchema,
  UuidSchema,
} from './common';

export const ApplicationSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  product: z.string(),
  company: z.string(),
  instance: z.string(),
  tokenPrefix: z.string(),
  status: ApplicationStatusSchema,
  environment: ApplicationEnvironmentSchema,
  createdAt: IsoTimestampSchema,
  lastLogAt: IsoTimestampSchema.nullable(),
  // Deployed code version reported on the instance's heartbeat (null until first seen).
  version: z.string().nullable(),
  versionUpdatedAt: IsoTimestampSchema.nullable(),
});

export const ApplicationWithStatsSchema = ApplicationSchema.extend({
  logs24h: z.number().int().nonnegative(),
  errorRate24h: z.number().min(0).max(1),
});

const IdentifierField = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Z0-9][A-Z0-9_]*$/i, 'letters, digits or underscores (no dashes)');

export const CreateApplicationSchema = z.object({
  product: IdentifierField,
  company: IdentifierField,
  instance: z
    .string()
    .max(64)
    .regex(/^([A-Z0-9][A-Z0-9_]*)?$/i, 'letters, digits or underscores (no dashes), or empty')
    .optional()
    .default(''),
  environment: ApplicationEnvironmentSchema.optional(),
});

export const UpdateApplicationSchema = z
  .object({
    name: z.string().trim().min(1).max(128).optional(),
    product: IdentifierField.optional(),
    company: IdentifierField.optional(),
    instance: z
      .string()
      .max(64)
      .regex(/^([A-Z0-9][A-Z0-9_]*)?$/i, 'letters, digits or underscores (no dashes), or empty')
      .optional(),
    status: ApplicationStatusSchema.optional(),
    environment: ApplicationEnvironmentSchema.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
  });

export const ApplicationCreatedResponseSchema = z.object({
  application: ApplicationSchema,
  rawToken: z.string(),
});

export type Application = z.infer<typeof ApplicationSchema>;
export type ApplicationWithStats = z.infer<typeof ApplicationWithStatsSchema>;
export type CreateApplication = z.infer<typeof CreateApplicationSchema>;
export type UpdateApplication = z.infer<typeof UpdateApplicationSchema>;
export type ApplicationCreatedResponse = z.infer<typeof ApplicationCreatedResponseSchema>;
